import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { CommonButton, CommonButtonV1, CommonModal, CommonModalV1 } from 'gemlux-common-components';
import Box from '@mui/material/Box';
import { differenceInDays } from 'date-fns';
import { CONTACT_US_ROUTE, LOGIN_ROUTE } from '../../../utils/routes';
import { theme } from '../../../theme/theme';
import { handleGoBackTo } from '../../../utils/general.utils';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { IProduct, subscriptionActions } from '../../../redux/subscription/subscriptionSlice';
import LoaderSvg from '../../../assets/img/dot-revolve.svg';
import PlanDetails from './PlanDetails/PlanDetails';
import Plan from './Plan/Plan';
import BasicPlanSvg from '../../../assets/img/tag.svg';
import PremiumPlanSvg from '../../../assets/img/crown.svg';
import CoporatePlanSvg from '../../../assets/img/briefcase.svg';
import { PlanType } from '../../../utils/enums/plan-type.enum';
import { UpdateType } from '../../../utils/enums/subscription-update-type.enum';
import { organizationActions } from '../../../redux/organization/organizationSlice';
import WarningRoundSvg from '../../../assets/img/warning-round-icon.svg';
import LogoLink from '../../LogoLink/LogoLink';

type ISubscriptionProps = {
  setActiveStep?: (_activeStep: number) => void;
  isUpdatable?: boolean;
  viewOnly?: boolean;
};

function Subscription({ setActiveStep, isUpdatable, viewOnly }: ISubscriptionProps) {
  const navigate = useNavigate();
  const { products, componentStatus } = useAppSelector((state) => state.subscription);
  const { organizationSubscription, updateOrganizationLoading, subscriptionUpdateSucess } = useAppSelector(
    (state) => state.organization,
  );
  const dispatch = useAppDispatch();
  const getProductsLoading = _.get(componentStatus, 'getProductsLoading', false);
  const [showUpdatePlanModal, setShowUpdatePlanModal] = useState<boolean>(false);
  const [updatedToPlan, setUpdatedToPlan] = useState<PlanType | null>(null);
  const [baseValue, setBaseValue] = useState<number | null>(null);
  const [updatedType, setUpdatedType] = useState<UpdateType | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>('');
  const [showDowngradeHoverModal, setShowDowngradeHoverModal] = useState(false);

  const handleDowngradeHover = () => {
    const activatedDateRange = differenceInDays(new Date(), new Date(organizationSubscription?.activatedAt ?? ''));

    if (activatedDateRange <= 30 && organizationSubscription?.activeSubscription.order !== '1') {
      setShowDowngradeHoverModal(true);
    }
  };

  useEffect(() => {
    if (products.length === 0) {
      dispatch(subscriptionActions.getProductsAsync());
    }
  }, []);

  if (getProductsLoading) {
    return <Box component="img" src={LoaderSvg} />;
  }

  const handleUpdateSubscription = () => {
    if (organizationSubscription?.organizationId) {
      // Prepare the subscription data based on baseSubscriptionAmount condition
      const subscriptionData = {
        organizationId: organizationSubscription.organizationId,
        requestedSubscriptionId: subscriptionId,
      };

      // Dispatch the action with the prepared data
      dispatch(
        organizationActions.updateOrganizationSubscriptionStart({
          organizationId: organizationSubscription.organizationId,
          data: subscriptionData,
          currentSubscription: organizationSubscription.activeSubscription,
        }),
      );
    }
  };

  const getPlanIcon = (planType: PlanType): string | undefined => {
    switch (planType) {
      case PlanType.BASIC:
        return BasicPlanSvg;
      case PlanType.PREMIUM:
        return PremiumPlanSvg;
      case PlanType.CORPORATE:
        return CoporatePlanSvg;
      default:
        break;
    }
  };

  return (
    <Grid display="flex" alignItems="center" flexDirection="column">
      {!isUpdatable && (
        <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
          <LogoLink width="173.343px" height="32px" />
          <Typography variant="footnoteRegular" paddingTop="20px" textAlign="center" color="black.primary">
            Porolis offers the following subscription plans :
          </Typography>
        </Box>
      )}

      <Grid container spacing={1} pt={6} justifyContent="center">
        <Grid item xs={3.5} sm={5.4} md={4.1} lg={3.1} mt={1.3}>
          <PlanDetails viewOnly={viewOnly} />
        </Grid>
        {products?.map((product: IProduct) => (
          <Grid item key={product.subscriptionId} xs={8} sm={4.6} md={3.4} lg={2.8}>
            <Plan
              type={product.name}
              setActiveStep={setActiveStep}
              product={product}
              isUpdatable={isUpdatable}
              viewOnly={viewOnly}
              setShowUpdatePlanModal={setShowUpdatePlanModal}
              setUpdatedToPlan={setUpdatedToPlan}
              setBaseValue={setBaseValue}
              setUpdatedType={setUpdatedType}
              setSubscriptionId={setSubscriptionId}
              onDowngradeHover={handleDowngradeHover}
            />
          </Grid>
        ))}
      </Grid>
      {!isUpdatable && (
        <Grid display="flex" flexDirection="column" gap={1} marginTop="20px">
          <Grid
            p="4px 24px"
            bgcolor="#FFFFFF"
            borderRadius="4px"
            display="flex"
            padding="16px 24px"
            alignItems="center"
          >
            <Typography variant="textBase">Need help choosing your perfect plan?</Typography>
            <CommonButton variant="text" onClick={() => window.open(CONTACT_US_ROUTE, '_blank')}>
              <Typography variant="textBase500" color="#3348FF">
                Contact Us
              </Typography>
            </CommonButton>
          </Grid>
          <CommonButton variant="text" onClick={() => handleGoBackTo(navigate, LOGIN_ROUTE)}>
            <Typography textAlign="center" color="#3348FF" variant="textBase500">
              Go Home
            </Typography>
          </CommonButton>
        </Grid>
      )}
      <CommonModalV1
        modal
        open={showDowngradeHoverModal}
        Icon={<Box component="img" src={WarningRoundSvg} />}
        title="Downgrade Unavailable!"
        subTitle="You subscribed less than 30 days ago. 
        You can downgrade only after 30 days, but you can upgrade at any time"
        handleClose={() => setShowDowngradeHoverModal(false)}
      >
        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
          <CommonButtonV1
            variant="contained"
            onClick={() => setShowDowngradeHoverModal(false)}
            fullWidth
            backgroundColor="#3348FF"
          >
            Dismiss
          </CommonButtonV1>
        </Box>
      </CommonModalV1>
      <CommonModalV1
        modal
        open={showUpdatePlanModal && !subscriptionUpdateSucess}
        Icon={<Box component="img" src={WarningRoundSvg} />}
        title="Confirm Plan Change"
        subTitle={`You'll be ${updatedType} to the`}
      >
        <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
          <Box display="flex" alignItems="center" flexDirection="column" gap={2}>
            <Box display="flex" alignItems="center" gap={2} marginTop={0}>
              {updatedToPlan && <Box component="img" src={getPlanIcon(updatedToPlan)} width={36} />}
              <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                <Typography variant="title3Bold">{updatedToPlan}</Typography>
                <Typography variant="textBase" color="#6F7C8E">
                  {updatedToPlan === PlanType.CORPORATE ? `Custom Pricing` : `USD ${baseValue}/mth`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="textBase" style={{ textAlign: 'center', marginBottom: 6, color: '#525E6F' }}>
              {` If you proceed, your subscription will be updated, and you will be charged according to the new plan.`}
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            mt={2}
            gap={2}
            paddingTop="30px"
            sx={{ width: '100%' }}
          >
            <CommonButton
              loading={updateOrganizationLoading}
              variant="contained"
              sx={{ width: '100%', fontSize: 15, fontWeight: 500, fontFamily: 'Inter', borderRadius: '8px' }}
              onClick={handleUpdateSubscription}
              backgroundColor="#3348FF"
              padding="8px 12px"
            >
              {updatedType === UpdateType.UPGRADED ? 'Yes, update' : 'Downgrade'}
            </CommonButton>
            <CommonButton
              variant="outlined"
              data-testid="cancel-button"
              sx={{
                width: '100%',
                fontSize: 15,
                fontWeight: 500,
                fontFamily: 'Inter',
                color: '#3348FF',
                borderColor: '#3348FF',
                borderRadius: '8px',
                '&.Mui-disabled': {
                  color: theme.palette.secondary.main,
                  borderColor: theme.palette.secondary.main,
                  backgroundColor: 'transparent',
                  opacity: 1,
                },
                padding: '8px 12px',
              }}
              disabled={updateOrganizationLoading}
              onClick={() => setShowUpdatePlanModal(false)}
            >
              Cancel
            </CommonButton>
          </Box>
        </Box>
      </CommonModalV1>
    </Grid>
  );
}

export default Subscription;
